import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ActionIcon, Button, Group, LoadingOverlay, Menu } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { isEqual } from "lodash";
import {
  ArrowBack,
  Copy,
  Settings,
  ExternalLink,
  Trash,
} from "tabler-icons-react";
import { UseMutation } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import { MutationDefinition } from "@reduxjs/toolkit/query";

import {
  ENTITY_DELETE_AUTHORISED_ORGANIZATIONS,
  ENTITY_DELETE_AUTHORISED_ROLES,
} from "_roles";
import { useOpenRemoveModal } from "common/components/useOpenRemoveModal";
import { throwError } from "common/api/apiHelpers";
import { AccessRights } from "./auth/AccessRights";
//import { openWindow } from "../utils/openWindow";

function EntityToolbar<T extends { id: string }>({
  entity,
  form,
  addMutation,
  updateMutation,
  removeMutation,
  label,
  children,
  showDuplicate = true,
}: {
  entity: T | undefined;
  form: UseFormReturnType<Omit<T, "id">>;
  addMutation: ReturnType<UseMutation<MutationDefinition<any, any, any, any>>>;
  updateMutation: ReturnType<
    UseMutation<MutationDefinition<any, any, any, any>>
  >;
  removeMutation: ReturnType<
    UseMutation<MutationDefinition<any, any, any, any>>
  >;
  label: string;
  showDuplicate?: boolean;
  children?: React.ReactNode;
}) {
  const isDirty = useMemo(() => !isEqual(entity, form.values), [entity, form]);

  const navigate = useNavigate();

  const [addEntity, addEntityStatus] = addMutation;
  const [updateEntity, updateEntityStatus] = updateMutation;
  const [removeEntity] = removeMutation;

  const pronoun = label[0] === "é" ? "cet" : "ce";
  const openRemoveModal = useOpenRemoveModal(
    `Delete ${pronoun} ${label} ?`,
    () => removeEntity(entity?.id),
    "./..",
  );

  const save = async () => {
    if (entity) {
      updateEntity({ id: entity.id, ...form.values });
    } else {
      const newEntity = await addEntity(form.values).then(throwError);
      navigate("./../" + newEntity.id);
    }
  };

  const duplicate = () =>
    navigate("./../new", {
      state: { from: { ...form.values, name: "eventDuplication" } },
    });

  const { eventId } = useParams<{ eventId: string }>();
  const isEventCreation = !eventId;

  return (
    <Group position={"right"}>
      <LoadingOverlay
        visible={addEntityStatus.isLoading || updateEntityStatus.isLoading}
      />
      {!isEventCreation && (
        <div
          role="link"
          className="text-button-admin"
          aria-labelledby="Logo d'ouverture d'un lien pour accéder à l'url de l'évènement"
        >
          <ExternalLink
            size={20}
            onClick={() =>
              //openWindow(`/event/${eventId}`, undefined, undefined, "_blank")
              window.open(`/event/${eventId}`, "_blank")
            }
          />
        </div>
      )}

      <Button
        className="bg-button-admin disabled:opacity-75"
        disabled={!isDirty}
        onClick={() => {
          if (form.validate().hasErrors) {
            return;
          }
          save();
        }}
      >
        Save
      </Button>

      {entity && (
        <Menu transition="pop" shadow="md" position={"bottom-end"}>
          <Menu.Target>
            <ActionIcon size={"lg"} className="text-button-admin">
              <Settings />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              icon={<ArrowBack size={14} />}
              disabled={!isDirty}
              onClick={() => {
                form.setValues(entity);
              }}
            >
              Annuler les changements
            </Menu.Item>
            {showDuplicate && (
              <Menu.Item icon={<Copy size={14} />} onClick={duplicate}>
                Dupliquer
              </Menu.Item>
            )}

            <AccessRights
              authorizedRoles={ENTITY_DELETE_AUTHORISED_ROLES}
              authorizedOrganizations={ENTITY_DELETE_AUTHORISED_ORGANIZATIONS}
            >
              <Menu.Item
                className="text-warning"
                icon={<Trash size={14} />}
                onClick={openRemoveModal}
              >
                Supprimer
              </Menu.Item>
            </AccessRights>

            {children && (
              <>
                <Menu.Divider />
                {children}
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}

export default EntityToolbar;
